import { Chip } from "@mui/material";
import { useEffect, useState } from "react";
import useEntitlements from "../../hooks/useEntitlements";
import { useSelector } from "../../store";
import calculateTrialDaysRemaining from "../../utils/calculateTrialDaysRemaining";

const ONE_HOUR_TO_MS = 3600000;

const OrganizationTier = ({
  alternatePremiumColor = false,
}: {
  alternatePremiumColor?: boolean;
}) => {
  const currentOrganization = useSelector(
    (state) => state.user.currentOrganization,
  );
  const { entitlements } = useEntitlements();

  const [trialDaysRemaining, setTrialDaysRemaining] = useState(-1);
  const [trialEnded, setTrialEnded] = useState(false);

  useEffect(() => {
    if (
      entitlements?.tier === "TRIAL" &&
      currentOrganization?.adminSettings?.trialEndDate
    ) {
      const days = calculateTrialDaysRemaining(currentOrganization);
      setTrialDaysRemaining(days);
    }

    const interval = setInterval(() => {
      const days = calculateTrialDaysRemaining(currentOrganization);
      setTrialDaysRemaining(days);
    }, ONE_HOUR_TO_MS);

    return () => clearInterval(interval);
  }, [currentOrganization, entitlements?.tier]);

  useEffect(() => {
    setTrialEnded(!trialDaysRemaining || trialDaysRemaining < 0);
  }, [trialDaysRemaining]);

  const tier = entitlements?.tier;

  if (!tier) return null;

  const chipProps = {
    size: "small" as const,
    variant: "outlined" as const,
    sx: {
      fontSize: "0.7rem",
      fontWeight: 500,
      borderRadius: "999px",
      height: 20,
      px: 0.25, // horizontal padding
      py: 0, // vertical padding not needed here
      ml: 0.75, // left margin
      lineHeight: 1.2,
      letterSpacing: "0.25px",
    },
  };

  if (tier === "PREMIUM") {
    return (
      <Chip
        {...chipProps}
        label="Premium"
        sx={{
          ...chipProps.sx,
          backgroundColor: alternatePremiumColor ? "#ff9c001a" : "#ffd7001a",
          border: "1px solid #d1d5db54",
          color: alternatePremiumColor ? "#e5a503" : "#FFD700",
        }}
      />
    );
  }

  if (tier === "ENTERPRISE") {
    return (
      <Chip
        {...chipProps}
        label="Enterprise"
        sx={{
          ...chipProps.sx,
          backgroundColor: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1d4ed8",
        }}
      />
    );
  }

  if (tier === "ESSENTIALS") {
    return (
      <Chip
        {...chipProps}
        label="Essentials"
        sx={{
          ...chipProps.sx,
          backgroundColor: "#22c55e1a", // soft green background
          border: "1px solid #d1d5db54",
          color: "#22c55e", // emerald-500 text
        }}
      />
    );
  }

  if (tier === "TRIAL") {
    return (
      <Chip
        {...chipProps}
        label={
          !trialEnded
            ? `Trial – ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? "s" : ""} left`
            : "Trial ended"
        }
        sx={{
          ...chipProps.sx,
          backgroundColor: "#ffe4e6",
          border: "1px solid	#fecdd3",
          color: "#b91c1c",
        }}
      />
    );
  }

  if (tier === "SCHEDULING") {
    return (
      <Chip
        {...chipProps}
        label="Scheduling"
        sx={{
          ...chipProps.sx,
          backgroundColor: "#f3e8ff",
          border: "1px solid #e9d5ff",
          color: "#7e22ce",
        }}
      />
    );
  }

  if (tier === "AI_SCRIBE") {
    return (
      <Chip
        {...chipProps}
        label="AI Scribe"
        sx={{
          ...chipProps.sx,
          backgroundColor: "#ecfeff",
          border: "1px solid #a5f3fc",
          color: "#0891b2",
        }}
      />
    );
  }

  // Default pill for custom/other tiers
  const formattedLabel =
    tier.charAt(0).toUpperCase() +
    tier.slice(1).toLowerCase().replace(/_/g, " ");

  return (
    <Chip
      {...chipProps}
      label={formattedLabel}
      sx={{
        ...chipProps.sx,
        backgroundColor: "#f4f4f5",
        border: "1px solid #e4e4e7",
        color: "#52525b",
      }}
    />
  );
};

export default OrganizationTier;
